import Button from "@/comps/button"
import { CustomThemedResource } from "@/comps/image"
import { Textarea } from "@/comps/textarea"
import { useCoins } from "@/utils/coins"
import { useShortCut } from "@kartyom/shared-components"
import axios from "axios"
import clsx from "clsx"
import { CombinedPopupsData } from "controllers/dashboard"
import { useTranslation } from "next-i18next"
import { StartSessionWithInputsRequest } from "pages/api/wizard-start-session-with-inputs"
import { useEffect, useRef, useState } from "react"
import { useDropzone } from "react-dropzone"
import {
	DropAreaForModel,
	getFormatsFromMimeTypes,
	MimeTypes,
	mimeTypes,
} from "../../comps/simple-drop-area"
import {
	ChatRequestImage,
	ChatRequestSourceMedia,
	ChatRequestVideo,
} from "../../pages/api/wizard-list-chat"
import { WizardUploadResponse } from "../../pages/api/wizard-upload"
import { assetUrl } from "../../utils/cdn"
import {
	getLoadedImage,
	getLoadedVideo,
} from "../../utils/video"
import { StylesPopup } from "./styles-popup"

export type InputsRequestType = {
	aspects: {
		id: string
		name: string
	}
	actors: {
		id: null | string
		name: string
		image: string | null
		gender: "male" | "female" | null
	}
	music: {
		id: string | null
		name: string
	}
	voices: {
		id: string
		name: string
		tags: string[]
		preview_url: string
		gender: "male" | "female" | null
	}
	captions: {
		id: string
		thumb_url: string
	}
}

type GeneralSelectorType = {
	info: ExtractResponse
	icon: string
	name: string
}

type VoiceSelector = GeneralSelectorType & {
	type: "voices"
	action: (
		id: string,
		name: string,
		tags: string[],
		preview_url: string,
		gender: "male" | "female" | null,
	) => void
}

type ActorSelector = GeneralSelectorType & {
	type: "actors"
	action: (
		id: string | null,
		name: string,
		image: string | null,
		gender: "male" | "female" | null,
	) => void
}

type MusicSelector = GeneralSelectorType & {
	type: "music"
	action: (id: string, name: string) => void
}

type AspectSelector = GeneralSelectorType & {
	type: "aspect"
	action: (id: string, name: string) => void
}

type CaptionSelector = GeneralSelectorType & {
	type: "captions"
	action: (id: string, thumb: string) => void
}

export type SelectorType =
	| VoiceSelector
	| ActorSelector
	| MusicSelector
	| AspectSelector
	| CaptionSelector

type Selected =
	| "txt_url_to_video"
	| "txt_medias_to_video"
	| "txt_script_to_video"
	| "txt_idea_to_video"

export interface PopupsProps {
	initialSelected: Selected
	close: () => void
	data: CombinedPopupsData | null
	setInputs: (v: StartSessionWithInputsRequest) => void
}

type MessageType = {
	txt_url_to_video: {
		type: "url"
		content: string
	}
	txt_idea_to_video: {
		type: "idea"
		content: string
	}
	txt_medias_to_video: {
		type: "medias"
		resources: ChatRequestSourceMedia[]
	}
	txt_script_to_video: {
		type: "script"
		content: string
	}
}

interface PopupsComponentProps {
	initialSelected: Selected
	close: () => void
	data: CombinedPopupsData
	setInputs: (v: StartSessionWithInputsRequest) => void
	setSubscriptionPopupOpen: (v: boolean) => void
}

export function Popups(props: PopupsComponentProps) {
	const {
		initialSelected,
		close,
		setInputs,
		setSubscriptionPopupOpen,
	} = props
	const [selected, setSelected] =
		useState<Selected>(initialSelected)
	const [message, setMessage] = useState<MessageType>({
		txt_url_to_video: {
			type: "url",
			content: "",
		},
		txt_idea_to_video: {
			type: "idea",
			content: "",
		},
		txt_medias_to_video: {
			type: "medias",
			resources: [] as ChatRequestSourceMedia[],
		},
		txt_script_to_video: {
			type: "script",
			content: "",
		},
	})
	const popupsHeaderRef = useRef<HTMLDivElement>(null)
	const { t } = useTranslation()
	const [selectedGender, setSelectedGender] = useState<
		"male" | "female" | null
	>(null)
	const wallet = useCoins()
	const [firstTime, setFirstTime] = useState(true)
	const [isStylePopupOpen, setStylePopupOpen] =
		useState(false)

	const tokensSummery =
		(wallet?.permanentTokens ?? 0) +
		(wallet?.temporaryTokens ?? 0)

	const [inputsRequest, setInputsRequest] =
		useState<InputsRequestType>({
			aspects: {
				id: props.data.aspects[0].id,
				name: props.data.aspects[0].name,
			},
			actors: {
				id: null,
				name: "No Actor",
				image: null,
				gender: null,
			},
			music: {
				id: null,
				name: "None",
			},
			voices: {
				id: props.data.voices[0].id,
				name: props.data.voices[0].name,
				gender: props.data.voices[0].gender,
				tags: props.data.voices[0].tags,
				preview_url: props.data.voices[0].preview_url,
			},
			captions: {
				id: props.data.captions[0].id,
				thumb_url: props.data.captions[0].thumb_url,
			},
		})
	const data = extractInputsResponse(props.data)

	useEffect(() => {
		if (firstTime) {
			setFirstTime(false)
			if (
				selected === "txt_medias_to_video" &&
				popupsHeaderRef.current
			) {
				popupsHeaderRef.current.scrollLeft = 400
			}
		}
	}, [firstTime, popupsHeaderRef, selected])

	const sections = [
		"txt_url_to_video",
		"txt_medias_to_video",
		"txt_script_to_video",
		"txt_idea_to_video",
	] as const
	const buttons: PopupButtonProps[] = sections.map(
		(section) => {
			return {
				name: section,
				action: () => setSelected(section),
				isSelected: selected === section,
			}
		},
	)

	const selectors: SelectorType[] = [
		{
			type: "actors",
			info: data[0],
			name: "txt_select_actor",
			icon: "/general/actor",
			action: (id, name, image, gender) =>
				setterNecessaryState({
					id,
					name,
					type: "actors",
					image,
					gender,
				}),
		},
		{
			type: "voices",
			info: data[1],
			name: "lbl_voiceover",
			icon: "/general/voiceover",
			action: (id, name, tags, preview_url, gender) =>
				setterNecessaryState({
					id,
					name,
					type: "voices",
					tags,
					preview_url,
					gender,
				}),
		},
		{
			type: "music",
			info: data[2],
			name: "txt_bg_music",
			icon: "/general/music",
			action: (id, name) =>
				setterNecessaryState({ id, name, type: "music" }),
		},
		{
			type: "aspect",
			info: data[3],
			name: "lbl_ratio",
			icon: "/general/ratio",
			action: (id, name) =>
				setterNecessaryState({ id, name, type: "aspects" }),
		},
		{
			type: "captions",
			info: data[4],
			name: "txt_captions_style",
			icon: "/general/ratio",
			action: (id, thumb) =>
				setterNecessaryState({
					id,
					thumb,
					type: "captions",
				}),
		},
	]

	type ActorsSetter = {
		id: string | null
		name: string
		type: "actors"
		image: string | null
		gender: "male" | "female" | null
	}

	type VoiceSetter = {
		id: string
		name: string
		type: "voices"
		preview_url: string
		tags: string[]
		gender: "male" | "female" | null
	}

	type MusicSetter = {
		id: string
		type: "music"
		name: string
	}

	type AspectsSetter = {
		id: string
		type: "aspects"
		name: string
	}

	type CaptionsSetter = {
		id: string
		type: "captions"
		thumb: string
	}

	type StateSetter =
		| ActorsSetter
		| VoiceSetter
		| MusicSetter
		| AspectsSetter
		| CaptionsSetter

	function setterNecessaryState(config: StateSetter) {
		const { type } = config
		switch (type) {
			case "actors":
				setInputsRequest((o) => {
					const { id, image, gender, name } = config
					return {
						...o,
						actors: {
							id,
							name,
							image: id === null ? null : image ?? null,
							gender: gender ?? null,
						},
					}
				})
				setSelectedGender(config.gender ?? null)
				const firstVoice = props.data.voices.find(
					(a) => a.gender === config.gender,
				)

				setInputsRequest((o) => {
					return {
						...o,
						voices: firstVoice
							? {
									id: firstVoice.id,
									name: firstVoice.name,
									gender: firstVoice.gender,
									preview_url: firstVoice.preview_url,
									tags: firstVoice.tags,
							  }
							: inputsRequest.voices,
					}
				})
				break

			case "voices":
				setInputsRequest((o) => {
					const { tags, id, name, gender, preview_url } =
						config

					return {
						...o,
						voices: {
							id,
							name,
							gender,
							tags,
							preview_url,
						},
					}
				})
				break

			case "music":
				setInputsRequest((o) => {
					const { id, name } = config
					return {
						...o,
						music: {
							id,
							name,
						},
					}
				})
				break

			case "aspects":
				setInputsRequest((o) => {
					const { id, name } = config

					return {
						...o,
						aspects: {
							id,
							name,
						},
					}
				})
				break
			case "captions":
				setInputsRequest((o) => {
					const { id, thumb } = config

					return {
						...o,
						captions: {
							id,
							thumb_url: thumb,
						},
					}
				})
				break
		}
	}

	useShortCut({
		shortCut: "Escape",
		handler: () => {
			if (isStylePopupOpen) return
			close()
		},
	})

	return (
		<div
			id="dashboard-popups"
			onClick={(e) => {
				const target = e.currentTarget
				if (target instanceof HTMLElement) {
					if (
						target.id === "dashboard-popups" &&
						!isStylePopupOpen
					) {
						close()
					}
				}
			}}
			className={clsx(
				"fixed z-[102] h-[100svh] w-screen items-center backdrop-blur-lg",
				"left-0 top-0 flex justify-center bg-color-popup",
			)}>
			<div
				onClick={(e) => e.stopPropagation()}
				className={clsx(
					"flex flex-col rounded-[10px] tablet:w-[680px]",
					"relative w-[351px] border border-color-separator",
					"h-[428px] bg-color-surface tablet:h-[404px] desktop:w-[690px]",
					isStylePopupOpen && "opacity-0",
				)}>
				<div
					ref={popupsHeaderRef}
					className={clsx(
						"relative flex items-center bg-color-popup-cell tablet:px-[12px]",
						"no-scrollbar w-full justify-between overflow-x-scroll rounded-t-[10px] tablet:justify-start",
						"h-[52px]",
					)}>
					{buttons.map((b) => (
						<PopupButton key={b.name} {...b} />
					))}

					<div
						style={{
							width: popupsHeaderRef.current?.scrollWidth
								? popupsHeaderRef.current?.scrollWidth +
								  "px"
								: "100%",
						}}
						className="absolute bottom-0 left-0 h-[1px] w-full bg-color-separator"></div>
				</div>

				<div
					className={clsx(
						"flex px-[12px]",
						"flex-col justify-center gap-[11px]",
						"w-full flex-1",
					)}>
					{data && (
						<PopupContent
							message={message[selected]}
							setMessage={setMessage}
							startAction={() => setStylePopupOpen(true)}
						/>
					)}
				</div>

				<div
					className={clsx(
						"w-full px-[12px] py-[20px]",
						"rounded-b-[10px] tablet:flex tablet:justify-center",
					)}>
					<Button
						primary
						className="w-full tablet:w-[340px]"
						text={t("lbl_next")}
						disabled={canStartSession(message[selected])}
						onClick={() => {
							setStylePopupOpen(true)
						}}
					/>
				</div>

				<button
					onClick={close}
					className={clsx(
						"hidden items-center justify-center tablet:flex",
						"absolute rounded-full border bg-color-popup-cell",
						"right-[-40px] top-0 aspect-1 w-[32px] border-color-separator",
					)}>
					<CustomThemedResource
						format="svg"
						source="/general/close"
					/>
				</button>
			</div>

			<StylesPopup
				isOpen={isStylePopupOpen}
				setIsOpen={setStylePopupOpen}
				selectors={selectors}
				selectedGender={selectedGender}
				inputsRequest={inputsRequest}
				submitData={() => {
					close()
					if (tokensSummery <= 0) {
						setSubscriptionPopupOpen(true)
						return
					}
					setInputs({
						aspect_id: inputsRequest.aspects.id,
						voice_id: inputsRequest.voices.id,
						actor_id: inputsRequest.actors.id,
						music_id: inputsRequest.music.id,
						caption_id: inputsRequest.captions.id,
						message: message[selected],
					})
				}}
			/>
		</div>
	)
}

interface PopupButtonProps {
	name: Selected
	action: () => void
	isSelected: boolean
}

function PopupButton(props: PopupButtonProps) {
	const { name, action, isSelected } = props
	const { t } = useTranslation()

	return (
		<button
			onClick={action}
			className={clsx(
				isSelected ? "text-primary-600" : "text-blue-700",
				"relative h-full flex-1 whitespace-nowrap px-[15px] text-[14px] font-600 leading-[20px] tablet:flex-none tablet:text-[16px]",
			)}>
			{t(name)}

			<div
				className={clsx(
					"absolute bottom-[0px] left-0 h-[1px] w-full bg-primary-500",
					"transition-opacity duration-300",
					!isSelected && "opacity-0",
				)}></div>
		</button>
	)
}

interface ContentProps {
	message: StartSessionWithInputsRequest["message"]
	startAction: () => void
	setMessage: React.Dispatch<
		React.SetStateAction<MessageType>
	>
}

function PopupContent(props: ContentProps) {
	const { message, setMessage, startAction } = props
	const { t } = useTranslation()
	const uploadedsRef = useRef<HTMLDivElement>(null)
	const maxCount = 10
	const [leftDistanse, setLeftDistance] = useState(0)

	const scrollImages = (scrollOffset: number) => {
		if (uploadedsRef.current) {
			uploadedsRef.current.scrollLeft += scrollOffset
			setLeftDistance(
				uploadedsRef.current?.scrollLeft + scrollOffset,
			)
		}
	}

	function removeUploadedFile(index: number) {
		if (message.type !== "medias") {
			return
		}
		const cleared = message.resources.filter(
			(_, i) => i !== index,
		)

		setMessage((o) => {
			return {
				...o,
				txt_medias_to_video: {
					type: "medias",
					resources: cleared,
				},
			}
		})
	}

	async function onDrop(files: File[]) {
		if (message.type !== "medias") {
			return
		}

		files
			.splice(0, maxCount - message.resources.length)
			.forEach(async (file) => {
				const isImage = file.type.includes("image")
				const extension = file.type.split("/").at(-1)
				const tempId = Math.random()

				if (isImage) {
					const imageElem = await getLoadedImage(file)

					setMessage((o) => {
						return {
							...o,
							txt_medias_to_video: {
								type: "medias",
								resources: [
									...o.txt_medias_to_video.resources,
									{
										media_type: "image",
										resource_url: URL.createObjectURL(file),
										aspect:
											imageElem.width / imageElem.height,
										loading: true,
										index: tempId,
									},
								],
							},
						}
					})

					const url = await axios
						.post<WizardUploadResponse>(
							"/api/wizard-upload",
							{ extension },
						)
						.then((r) => r.data)
						.catch(() => console.error("upload faile case"))

					await axios.put(url?.upload ?? "", file)

					replaceGettedMedia({
						media_type: "image",
						resource_url:
							url?.download ?? URL.createObjectURL(file),
						aspect: imageElem.width / imageElem.height,
						failed:
							url?.download === undefined
								? true
								: undefined,
						index: tempId,
					})
				} else {
					const video = await getLoadedVideo(file)

					setMessage((o) => {
						return {
							...o,
							txt_medias_to_video: {
								type: "medias",
								resources: [
									...o.txt_medias_to_video.resources,
									{
										media_type: "video",
										resource_url: URL.createObjectURL(file),
										aspect:
											video.videoWidth / video.videoHeight,
										thumb_url: null,
										loading: true,
										index: tempId,
									},
								],
							},
						}
					})

					const url = await axios
						.post<WizardUploadResponse>(
							"/api/wizard-upload",
							{ extension },
						)
						.then((r) => r.data)
						.catch(() => console.error("upload faile case"))

					await axios.put(url?.upload ?? "", file)

					replaceGettedMedia({
						media_type: "video",
						resource_url:
							url?.download ?? URL.createObjectURL(file),
						aspect: video.videoWidth / video.videoHeight,
						thumb_url: null,
						failed:
							url?.download === undefined
								? true
								: undefined,
						index: tempId,
					})
				}
			})
	}

	function replaceGettedMedia(
		newMedia: ChatRequestImage | ChatRequestVideo,
	) {
		setMessage((o) => {
			return {
				...o,
				txt_medias_to_video: {
					type: "medias",
					resources: o.txt_medias_to_video.resources.map(
						(res) => {
							if (newMedia.index === res.index) {
								return newMedia
							}
							return res
						},
					),
				},
			}
		})
	}

	const dropzoneState = useDropzone({
		accept: getFormatsFromMimeTypes([
			...mimeTypes["images"],
			...mimeTypes["videos"],
		]),
		onDrop,
		maxFiles: maxCount,
		noClick: false,
	})

	switch (message.type) {
		case "medias":
			const haveFiles = message.resources.length !== 0
			const formats = [
				"image/jpeg",
				"image/png",
				"video/mp4",
				"image/webp",
			] as MimeTypes[]

			const backgroundImage = clsx(
				`url("data:image/svg+xml,%3csvg width='100%25' height='100%25'`,
				`xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25'`,
				`fill='none' rx='8' ry='8' stroke='%23B699FA' stroke-width='5'`,
				`stroke-dasharray='6%2c 14' stroke-dashoffset='14' stroke-linecap='round'/%3e%3c/svg%3e")`,
			)

			return (
				<div className="relative flex flex-col gap-[12px]">
					<span
						className={clsx(
							haveFiles && "hidden",
							"text-[16px] font-400 text-blue-600",
						)}>
						{t("txt_generate_video_medias")}
					</span>

					{haveFiles ? (
						<div
							ref={uploadedsRef}
							className={clsx(
								"no-scrollbar flex w-full animate-scale gap-[12px] scroll-smooth",
								"relative overflow-x-scroll border border-color-separator p-[16px]",
								"rounded-[9px] bg-color-surface-cell",
							)}>
							<DropAreaForModel
								state={dropzoneState}
								openExplorer={dropzoneState.open}
								formats={formats}
								border={true}
								className={clsx(
									message.resources.length >= maxCount &&
										"hidden",
									"h-[128px] min-w-[128px] max-w-[128px] rounded-[6px]",
								)}
								noText={true}
								backgroundImage={backgroundImage}
							/>
							{message.resources.map((resource, index) => {
								return (
									<div
										className={clsx(
											"group relative h-[128px] min-w-[128px] rounded-[6px] bg-blue-100",
											"flex max-w-[128px] items-center justify-center",
										)}
										key={resource.resource_url}>
										{resource.media_type === "image" ? (
											<>
												<img
													src={resource.resource_url}
													alt="user uploaded file"
													className="h-auto max-h-full overflow-hidden rounded-[6px] object-cover"
													style={{
														aspectRatio: resource.aspect,
													}}
												/>
												{resource.loading && (
													<div
														className={clsx(
															"absolute left-0 top-0 h-full w-full",
															"flex items-center justify-center rounded-[6px] bg-color-popup",
														)}>
														<div className="animate-spin">
															<img
																src={assetUrl(
																	"/general/loading-dark.webp",
																)}
																alt="loading state"
																className="h-[18px] w-[18px]"
															/>
														</div>
													</div>
												)}
											</>
										) : (
											<>
												<video
													src={
														resource.resource_url +
														"#t=0.001"
													}
													className="h-auto max-h-full overflow-hidden rounded-[6px] object-cover"
													style={{
														aspectRatio: resource.aspect,
													}}
												/>
												<img
													className={clsx(
														"absolute bottom-[8px] left-[8px]",
														"h-[24px] w-[24px]",
													)}
													alt="play icon"
													src={assetUrl(
														"/general/play-white.png",
													)}
												/>
												{resource.loading && (
													<div
														className={clsx(
															"absolute left-0 top-0 h-full w-full",
															"flex items-center justify-center rounded-[6px] bg-color-popup",
														)}>
														<div className="animate-spin">
															<CustomThemedResource
																format="webp"
																source="/general/loading"
																className="h-[18px] w-[18px]"
															/>
														</div>
													</div>
												)}
											</>
										)}

										{resource.failed && (
											<div
												className={clsx(
													"absolute left-0 top-0 h-full w-full",
													"flex items-center justify-center rounded-[6px] bg-color-popup",
												)}>
												<img
													src={assetUrl(
														"/general/warning-white.png",
													)}
													alt="warning white"
													className="h-[24px] w-[24px]"
												/>
											</div>
										)}

										<button
											onClick={() =>
												removeUploadedFile(index)
											}
											className={clsx(
												"absolute right-[8px] top-[8px] h-[28px] w-[28px]",
												"rounded-full bg-color-cell hover:bg-primary-100",
												"cursor-pointer items-center justify-center border",
												"hidden border-color-separator group-hover:flex",
											)}>
											<CustomThemedResource
												source="/general/minus-gray"
												format="svg"
												alt="minus icon"
											/>
										</button>
									</div>
								)
							})}
						</div>
					) : (
						<DropAreaForModel
							state={dropzoneState}
							openExplorer={dropzoneState.open}
							border={true}
							formats={formats}
							className={clsx(
								"h-[182px] w-full rounded-[8px] desktop:h-[200px]",
							)}
							backgroundImage={backgroundImage}
						/>
					)}
					<button
						className={clsx(
							leftDistanse >= 610 ||
								!haveFiles ||
								message.resources.length < 5
								? "desktop:hidden"
								: "desktop:flex",
							"absolute hidden rounded-full bg-color-cell",
							"right-5 top-[55px] p-[4px] outline-none",
							"hover:bg-blue-100 disabled:bg-color-background",
						)}
						onClick={() => scrollImages(200)}>
						<CustomThemedResource
							source="/general/arrow"
							format="svg"
							className="h-[20px] w-[20px]"
						/>
					</button>
					<button
						className={clsx(
							leftDistanse <= 45 ||
								!haveFiles ||
								message.resources.length < 5
								? "desktop:hidden"
								: "desktop:flex",
							"absolute hidden rounded-full bg-color-cell",
							"left-5 top-[55px] p-[4px] outline-none",
							"hover:bg-blue-100 disabled:bg-color-background",
						)}
						onClick={() => scrollImages(-200)}>
						<CustomThemedResource
							source="/general/arrow"
							format="svg"
							className="h-[20px] w-[20px] rotate-180"
						/>
					</button>
				</div>
			)
		case "script":
		case "idea":
			return (
				<>
					<span className="text-[16px] font-400 text-blue-600">
						{message.type === "idea"
							? t("txt_generate_with_imagination")
							: t("txt_generate_with_script")}
					</span>

					<div className="rounded-[10px] bg-gradient-to-r from-[#5DA2F6] to-[#B15DF6] p-[2px]">
						<div
							className={clsx(
								"relative flex items-center gap-[4px] bg-color-cell",
								"h-[245px] rounded-[8px] px-[11px] py-[14px] tablet:h-[203px]",
							)}>
							<Textarea
								placeholder={
									message.type === "idea"
										? t("txt_write_your_idea")
										: t("txt_write_your_script")
								}
								value={message.content}
								setValue={(v: string) =>
									setMessage((o) => {
										if (message.type === "idea") {
											return {
												...o,
												txt_idea_to_video: {
													type: message.type,
													content: v,
												},
											}
										}
										if (message.type === "script") {
											return {
												...o,
												txt_script_to_video: {
													type: message.type,
													content: v,
												},
											}
										}
										return o
									})
								}
								maxCount={3000}
								onKeyDown={(e) => {
									if (message.content === "") return

									if (e.key === "Enter") {
										startAction()
									}
								}}
							/>
						</div>
					</div>
				</>
			)

		case "url":
			return (
				<>
					<span className="text-[16px] font-400 text-blue-600">
						{t("txt_generate_video_ads")}
					</span>

					<div className="rounded-[10px] bg-gradient-to-r from-[#5DA2F6] to-[#B15DF6] p-[2px]">
						<div
							className={clsx(
								"flex items-center gap-[4px] bg-color-cell",
								"rounded-[8px] px-[11px] py-[14px]",
							)}>
							<CustomThemedResource
								source="/general/url-icon"
								format="svg"
								className="h-[16px] w-[16px]"
							/>
							<input
								type="url"
								placeholder={t("txt_amazon_link")}
								value={message.content}
								className={clsx(
									"flex-1 text-[16px] placeholder:text-color-placeholder",
									"bg-color-cell text-blue-800 outline-none",
								)}
								onChange={(e) => {
									if (e.target) {
										setMessage((o) => {
											return {
												...o,
												txt_url_to_video: {
													type: "url",
													content: e.target.value,
												},
											}
										})
									}
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										const trimmedContent =
											message.content.trim()

										if (trimmedContent.length === 0) {
											return
										}

										try {
											new URL(trimmedContent)
											startAction()
										} catch {
											e.preventDefault()
										}
									}
								}}
							/>
						</div>
					</div>
				</>
			)
	}
}

type ExtractResponse = {
	type:
		| "aspects"
		| "music"
		| "voices"
		| "actors"
		| "captions"
	data: {
		id: string | null
		name: string
		thumbnail: string | null
		animated_thumbnail: string | null
		preview_url: string | null
		tags: string[] | null
		gender: "male" | "female" | null
	}[]
}

function extractInputsResponse(
	input: CombinedPopupsData,
): ExtractResponse[] {
	return [
		{
			type: "actors",
			data: [
				{
					id: null,
					name: "No Actor",
					thumbnail: null,
					animated_thumbnail: null,
					preview_url: null,
					tags: null,
					gender: null,
				},
				...input.actors.map((a) => {
					return {
						...a,
						preview_url: null,
						tags: null,
					}
				}),
			],
		},
		{
			type: "voices",
			data: input.voices.map((a) => {
				return {
					...a,
					thumbnail: null,
					animated_thumbnail: null,
				}
			}),
		},
		{
			type: "music",
			data: [
				{
					id: null,
					name: "None",
					thumbnail: null,
					animated_thumbnail: null,
					preview_url: null,
					tags: null,
					gender: null,
				},
				...input.music.map((a) => {
					return {
						...a,
						thumbnail: null,
						animated_thumbnail: null,
						preview_url: null,
						tags: null,
						gender: null,
					}
				}),
			],
		},
		{
			type: "aspects",
			data: input.aspects.map((a) => {
				return {
					...a,
					thumbnail: null,
					animated_thumbnail: null,
					preview_url: null,
					tags: null,
					gender: null,
				}
			}),
		},
		{
			type: "captions",
			data: input.captions.map((a) => {
				return {
					...a,
					name: "",
					thumbnail: a.thumb_url,
					animated_thumbnail: null,
					preview_url: null,
					tags: null,
					gender: null,
				}
			}),
		},
	]
}

function canStartSession(
	message: StartSessionWithInputsRequest["message"],
): boolean {
	switch (message.type) {
		case "medias":
			return (
				message.resources.length === 0 ||
				message.resources.filter(
					(e) => e.failed || e.loading,
				).length !== 0
			)

		case "url":
			try {
				new URL(message.content)
				return message.content.trim().length === 0
			} catch (e) {
				return true
			}
		case "idea":
		case "script":
			return message.content.trim().length === 0
	}
}
